import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, X, HelpCircle, Gift, Heart } from 'lucide-react';

const pricingFeatures = [
  { feature: "Dedicated Concierge", included: true },
  { feature: "Post-Close Email Outreach", included: true },
  { feature: "Personalized Phone Follow-Up", included: true },
  { feature: "Gift Coordination & Fulfillment", included: true },
  { feature: "Review Generation (Multi-Platform)", included: true },
  { feature: "Referral Capture & Routing", included: true },
  { feature: "30-Day Follow-Up Sequence", included: true },
  { feature: "Monthly Activity Reports", included: true },
  { feature: "Branded Communications", included: true },
  { feature: "Priority Support", included: true }
];

const clientBenefits = [
  "Personalized closing gift of their choice",
  "Lifetime loan monitoring & rate alerts",
  "Home value tracking updates",
  "Dedicated post-closing support",
  "VIP referral network access",
  "Proactive savings opportunities"
];

const faqs = [
  {
    question: "When do I pay?",
    answer: "Payment is collected after the loan closes. We invoice on a monthly basis for all closed loans from the previous month."
  },
  {
    question: "How do I submit closed loans?",
    answer: "We provide a simple secure portal where you can submit loan details in about 30 seconds. You can also integrate with your CRM for automatic submissions."
  },
  {
    question: "Are there any setup fees or minimums?",
    answer: "No setup fees, no monthly minimums, no long-term contracts. You only pay for the loans we service."
  },
  {
    question: "What if the borrower doesn't respond?",
    answer: "Our fee applies regardless of client engagement—we do the work either way. However, our proven sequences are designed to maximize response rates."
  }
];

export const PricingPage = () => {
  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#0F0F0F]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="h-[2px] w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Simple Pricing</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              One Price. <span className="text-[#E5D5A5]">Everything Included.</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              No hidden fees. No complicated tiers. Just white-glove service for every closed loan.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Card */}
      <section data-testid="pricing-card-section" className="py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-gradient-to-b from-[#1A1A1A] to-[#0F0F0F] border border-[#C9A227]/50 p-8 md:p-12 shadow-[0_0_60px_rgba(201,162,39,0.15)]"
          >
            <div className="text-center mb-10">
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Full-Service Package</p>
              <div className="flex items-baseline justify-center mb-2">
                <span className="font-serif text-7xl md:text-8xl font-bold text-white">$249</span>
              </div>
              <p className="text-neutral-400 text-xl">per closed loan</p>
              <p className="text-neutral-500 text-sm mt-2">Flat fee • No subscriptions • No hidden costs</p>
            </div>

            <div className="h-px bg-white/10 mb-10" />

            <div className="grid lg:grid-cols-2 gap-10">
              {/* Broker Benefits */}
              <div>
                <h3 className="font-serif text-xl text-[#E5D5A5] mb-6 flex items-center">
                  <span className="w-8 h-8 bg-[#C9A227]/20 rounded-full flex items-center justify-center mr-3">
                    <Check className="w-4 h-4 text-[#C9A227]" />
                  </span>
                  What You Get
                </h3>
                <div className="space-y-3">
                  {pricingFeatures.map((item) => (
                    <div 
                      key={item.feature}
                      data-testid={`pricing-feature-${item.feature.toLowerCase().replace(/\s+/g, '-')}`}
                      className="flex items-center text-neutral-300 text-sm"
                    >
                      <Check className="w-4 h-4 text-[#C9A227] mr-3 flex-shrink-0" />
                      {item.feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* Client Benefits */}
              <div>
                <h3 className="font-serif text-xl text-[#E5D5A5] mb-6 flex items-center">
                  <span className="w-8 h-8 bg-[#C9A227]/20 rounded-full flex items-center justify-center mr-3">
                    <Heart className="w-4 h-4 text-[#C9A227]" />
                  </span>
                  What Your Clients Get
                </h3>
                <div className="space-y-3">
                  {clientBenefits.map((benefit) => (
                    <div 
                      key={benefit}
                      className="flex items-center text-neutral-300 text-sm"
                    >
                      <Gift className="w-4 h-4 text-[#C9A227] mr-3 flex-shrink-0" />
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="h-px bg-white/10 my-10" />

            <div className="text-center">
              <Link to="/contact" data-testid="pricing-cta">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-12 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Get Started Today
                </Button>
              </Link>
              <p className="text-neutral-500 text-sm mt-4">No credit card required • Book a demo first</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ROI Calculator Teaser */}
      <section className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">The Math</p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
                Worth the <span className="text-[#E5D5A5]">Investment</span>
              </h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-8">
                Consider the value: If just one referral converts from our service, the return 
                far exceeds the $249 investment. Our partners consistently report that the 
                service helps increase their referral business significantly.
              </p>
              <div className="space-y-4 text-neutral-400">
                <p className="flex items-center">
                  <span className="text-[#C9A227] font-semibold mr-2">$249</span>
                  per loan investment
                </p>
                <p className="flex items-center">
                  <span className="text-[#C9A227] font-semibold mr-2">$3,000+</span>
                  typical commission on a referral
                </p>
                <p className="flex items-center">
                  <span className="text-[#C9A227] font-semibold mr-2">12x+</span>
                  potential return on investment
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-[#1A1A1A] border border-white/5 p-8"
            >
              <p className="text-xs text-[#C9A227] uppercase tracking-wider mb-6">Sample Scenario</p>
              <div className="space-y-6">
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <span className="text-neutral-400">Loans closed per month</span>
                  <span className="text-white font-semibold">10</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <span className="text-neutral-400">Monthly investment</span>
                  <span className="text-white font-semibold">$2,490</span>
                </div>
                <div className="flex justify-between items-center border-b border-white/5 pb-4">
                  <span className="text-neutral-400">Referrals captured*</span>
                  <span className="text-white font-semibold">2-3 / month</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-neutral-400">Potential return*</span>
                  <span className="text-[#C9A227] font-bold text-xl">$6,000 - $9,000</span>
                </div>
              </div>
              <p className="text-neutral-600 text-xs mt-6">
                *Results vary. This is a sample scenario and not a guarantee of specific outcomes.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing FAQs */}
      <section data-testid="pricing-faq-section" className="py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Questions</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              Pricing <span className="text-[#E5D5A5]">FAQs</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`pricing-faq-${index}`}
                className="bg-[#1A1A1A] border border-white/5 p-6 hover:border-[#C9A227]/30 transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <HelpCircle className="w-5 h-5 text-[#C9A227] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-medium mb-2">{faq.question}</h3>
                    <p className="text-neutral-400 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <p className="text-neutral-400 mb-4">Have more questions?</p>
            <Link to="/faq" data-testid="pricing-more-faqs">
              <Button variant="outline" className="bg-transparent border border-white/20 text-white hover:border-[#C9A227] hover:text-[#C9A227] rounded-none px-8 py-4 font-semibold text-sm tracking-wider uppercase transition-all duration-300">
                View All FAQs
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#141414] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to <span className="text-[#E5D5A5]">Get Started?</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Join hundreds of mortgage professionals who trust Better Closings 
              to deliver an exceptional post-close experience.
            </p>
            <Link to="/contact" data-testid="pricing-final-cta">
              <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-12 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                Book Your Demo
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
